import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import AbortableRequest from 'components/Explorer/models/abortable_request';
import HelpLink from 'components/Explorer/AdvancedSearch/HelpLink';
import LoadingIcon from 'components/Explorer/SearchLoadingIcon';
import Button from 'components/Shared/Button';

import { MAX_SELECTED } from './constants';
import ResultRow from './ResultRow';
import SelectedList from './SelectedList';

const Loading = {
  NOT_STARTED: 'not-started',
  IN_PROGRESS: 'in-progress',
  ERROR: 'error',
  EMPTY_RESPONSE: 'empty-response',
  SUCCESS: 'success'
};

const RESULTS_ID = 'ModalPicker-results';

// A row is a parent when the organisation has descendants worth offering, not
// when the search happened to match some of them: the same organisation used
// to render as a plain row or as a tree depending on the term typed. Results
// without a has_children flag (consumers with no hierarchy) keep the old
// behaviour.
const hasChildren = (item) => item.has_children === true || (item.children?.length ?? 0) > 0;

// Every Explorer JSON request identifies itself the same way; shared by the
// search and the children fetch below.
const JSON_HEADERS = {
  'X-Requested-With': 'XMLHttpRequest',
  'Content-Type': 'application/json',
  Accept: 'application/json'
};

const Editor = ({
  initiallySelected = [],
  initialSearch = '',
  titleId,
  i18nBase,
  analyticsDialogueName,
  autocompletePath,
  // Endpoint serving one page of an organisation's descendants. Consumers
  // without a hierarchy (the researcher picker) leave it null and get the
  // flat list they had.
  childrenPath = null,
  trackEvent,
  onCancel,
  onSave,
  maxSelected = MAX_SELECTED,
  helperText = [],
  renderResultMeta = null,
  renderResultExtras = null,
  renderNoResults = null,
  renderSelectedItemExtras = null,
  // Adapts the raw autocomplete response body into the results array plus an
  // opaque `meta` object passed on to getResultGroups. Defaults to treating
  // the body as the bare results array. ResearcherFilter supplies one to
  // unwrap its { organisation_name, researchers } envelope.
  parseResponse = (body) => ({ results: body ?? [], meta: null }),
  // Optionally partitions the visible rows into labelled sections. Receives the
  // visible rows (each carrying its absolute index) and the parsed `meta`, and
  // returns an ordered [{ key, label, rows }] preserving row order, or null for
  // a flat list. ResearcherFilter uses it to float own-organisation matches
  // into a "From [organisation]" section above the rest.
  getResultGroups = null,
  // Escape hatch that replaces the entire SelectedList region. Currently
  // used only by ResearcherFilter to preserve its pre-migration inline
  // selected list (h2 heading + empty-state paragraph + per-item profile
  // link) until the harmonised modal-selection UI is adopted.
  // Once that happens, remove the override in ResearcherFilter and
  // it will fall back to the default SelectedList; the prop itself can stay
  // for future consumers with bespoke selection UI.
  renderSelectedList = null
}) => {
  const searchInputRef = useRef(null);
  // Tracks whether the chip-driven auto-select has already had its single shot
  // at the first response. Held in a ref so the response handler sees a stable
  // mutable flag, not stale closure state.
  const autoSelectAttemptedRef = useRef(false);
  const [searchResults, setSearchResults] = useState([]);
  // Opaque per-response metadata from parseResponse (e.g. the researcher
  // picker's organisation name), forwarded to getResultGroups.
  const [responseMeta, setResponseMeta] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [selected, setSelected] = useState(initiallySelected);
  const [selectionIndex, setSelectionIndex] = useState(null);
  const [loading, setLoading] = useState(Loading.NOT_STARTED);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Per-parent paging state for the lazily-loaded descendants:
  // { loading, error, page, fetched, total }. `fetched` counts rows the
  // endpoint returned, so it stays comparable with its `total` even though
  // the rendered children also include the ones the search itself matched.
  const [childFetch, setChildFetch] = useState({});
  // In-flight children requests, so a new search can abort them before its
  // results replace the rows they were going to land in.
  const childRequestsRef = useRef(new Map());

  // One Set per selection change, reused by every per-row checkbox and lock
  // computation below. Rebuilding it inside parentCheckboxState/isSelected made
  // those O(selected) on each call, and the render loop calls them per visible
  // row (and again per result inside isChildLocked).
  const selectedIds = useMemo(() => new Set(selected.map((entry) => entry.id)), [selected]);

  // Resolve every parent's checkbox state once per selection change, plus the
  // set of children locked by a checked parent. parentCheckboxState and
  // isChildLocked are both read per visible row, and isChildLocked scans every
  // result for each child, so recomputing a parent's state inside those calls
  // was O(visibleRows × searchResults) per render; these lookups make it
  // constant-time.
  //
  // A parent reads as checked only via its own id: selecting every child
  // individually leaves it mixed, never checked, because `children` here is the
  // filtered search result rather than the parent's full child set, so
  // promoting it would imply children that were never shown. A checked parent
  // locks every child it contains in every subtree the child appears in — the
  // same GRID can sit under several roots (multi-rooted institutions), and
  // selecting one root implies the child everywhere it appears.
  const { parentStateById, lockedChildIds } = useMemo(() => {
    const states = new Map();
    const locked = new Set();

    searchResults.forEach((result) => {
      const children = Array.isArray(result.children) ? result.children : [];

      let state;
      if (selectedIds.has(result.id)) {
        state = 'checked';
      } else if (children.some((child) => selectedIds.has(child.id))) {
        state = 'mixed';
      } else {
        state = 'unchecked';
      }

      states.set(result.id, state);
      if (state === 'checked') children.forEach((child) => locked.add(child.id));
    });

    return { parentStateById: states, lockedChildIds: locked };
  }, [searchResults, selectedIds]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Forgets every subtree's lazy state: aborts the children requests in flight
  // and drops which parents were expanded and how far each was paged.
  function resetSubtrees() {
    childRequestsRef.current.forEach((request) => request.abort());
    childRequestsRef.current.clear();
    setExpandedIds(new Set());
    setChildFetch({});
  }

  useEffect(() => {
    setSelectionIndex(null);

    if (!searchTerm.length) {
      setLoading(Loading.NOT_STARTED);
      setSearchResults([]);
      setResponseMeta(null);
      return;
    }

    let cancelled = false;
    let searchRequest;

    // Debounce so a burst of keystrokes doesn't fire a request each. The client
    // aborts superseded requests, but every one still reaches the search service
    // (full-text + trigram + count join), and the cheapest queries to drop are
    // the short prefixes that match the most rows — so they wait the longest,
    // mirroring AutocompleteField's delayShortQuery behaviour.
    const debounce = searchTerm.length < 3 ? 500 : 200;
    const timer = setTimeout(() => {
      setLoading(Loading.IN_PROGRESS);

      searchRequest = new AbortableRequest(`${autocompletePath}?q=${encodeURIComponent(searchTerm)}`, {
        headers: JSON_HEADERS
      });

      searchRequest.then((error, response) => {
        // The cleanup below aborts in-flight requests on subsequent keystrokes;
        // those rejections aren't real failures and must not flip the modal into
        // the error state, otherwise the dropdown briefly shows an error message
        // until the newer request resolves.
        if (cancelled) return;
        // The old rows stay interactive through the debounce, so a subtree
        // expanded there has state recorded after the cleanup below ran. The
        // rows it describes are about to be replaced, and a parent left marked
        // as fully fetched would render empty and never fetch again.
        resetSubtrees();
        if (error) {
          setSearchResults([]);
          setLoading(Loading.ERROR);
          return;
        }
        // AbortableRequest yields an undefined body for an empty 200; parseResponse
        // defaults to treating the body as the bare results array and tolerates
        // undefined, so consumers needn't guard it themselves.
        const { results, meta } = parseResponse(response.body);
        setSearchResults(results);
        setResponseMeta(meta);
        setLoading(results.length === 0 ? Loading.EMPTY_RESPONSE : Loading.SUCCESS);

        autoSelectExactMatch(results);
      });
    }, debounce);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      searchRequest?.abort();
      // Runs before the next effect body on a new search, and on unmount --
      // the modal can be closed mid-fetch, and the callback would otherwise
      // resolve against a component that is no longer there.
      resetSubtrees();
    };
  }, [searchTerm, autocompletePath]);

  // When the picker is launched with a chip-driven initialSearch term, the
  // first response gets a single chance to auto-tick a result. Only an
  // unambiguous single match (by case-insensitive name or by id) auto-selects;
  // anything else leaves the user to choose.
  function autoSelectExactMatch(results) {
    if (autoSelectAttemptedRef.current) return;
    if (!initialSearch || !results?.length) return;

    autoSelectAttemptedRef.current = true;

    const lowerTerm = initialSearch.toLowerCase();
    const matches = results.filter((result) => result.name.toLowerCase() === lowerTerm || result.id === initialSearch);
    if (matches.length !== 1) return;

    const match = matches[0];
    setSelected((current) => {
      if (current.some((entry) => entry.id === match.id)) return current;
      // Honour the cap so a chip-driven open against an already-full
      // selection can't push us to 101. Skipped when the consumer has
      // disabled the cap by passing maxSelected={null}.
      if (maxSelected !== null && current.length >= maxSelected) return current;
      return [...current, { id: match.id, name: match.name }];
    });
  }

  function toggleSelected(item, isSelected) {
    if (isSelected) {
      setSelected((current) => [...current, { id: item.id, name: item.name }]);
      trackEvent('Item selected', { value: item.name });
    } else {
      setSelected((current) => current.filter((entry) => entry.id !== item.id));
      trackEvent('Item deselected', { value: item.name });
    }
  }

  function toggleParentSelected(parent, _wantChecked) {
    const visibleChildCount = (parent.children ?? []).length;
    const state = parentCheckboxState(parent);

    // A child is sole-parent when this is the only result it sits under. Such
    // children belong entirely to this parent; children that also appear under
    // another root are shared and must be handled independently. Rows carry
    // `shared` because the other roots' subtrees are loaded lazily, so scanning
    // the visible rows alone would read a multi-rooted child as sole-parent and
    // absorb a selection the user made under one of its other parents.
    const otherParents = searchResults.filter((result) => result.id !== parent.id);
    const sharedElsewhere = (child) =>
      child.shared === true ||
      otherParents.some(
        (result) => Array.isArray(result.children) && result.children.some((sibling) => sibling.id === child.id)
      );
    const soleParentChildIds = new Set(
      (parent.children ?? []).filter((child) => !sharedElsewhere(child)).map((child) => child.id)
    );

    if (state === 'checked') {
      // Deselecting a parent clears its own selection and the children beneath it,
      // so toggling a sole parent off empties its whole branch. The exception is a
      // child shared with another parent in the results: deselecting one of its
      // parents must not pull it out of the others, so it stays selected and those
      // parents simply fall back to mixed. Sole-parent children have nowhere to
      // remain, so they clear with the parent.
      setSelected((current) => current.filter((entry) => entry.id !== parent.id && !soleParentChildIds.has(entry.id)));
      trackEvent('Parent deselected', { value: parent.name, children_count: visibleChildCount });
      return;
    }

    // The parent now represents and locks all its children, so any sole-parent
    // child that was selected individually collapses into it rather than
    // lingering as its own entry in the selected-list. Shared children stay as
    // their own selection — the parent doesn't own them outright, and dropping
    // them here would strand them out of the other root they sit under.
    setSelected((current) => {
      if (current.some((entry) => entry.id === parent.id)) return current;
      // Absorbing the sole-parent children frees their slots, so test the cap
      // against the post-absorb length — the parent replaces them rather than
      // adding on top.
      const withoutAbsorbedChildren = current.filter((entry) => !soleParentChildIds.has(entry.id));
      if (maxSelected !== null && withoutAbsorbedChildren.length >= maxSelected) return current;
      return [...withoutAbsorbedChildren, { id: parent.id, name: parent.name }];
    });
    // A checked parent locks and implies every child, so collapse the branch to
    // keep the now-redundant child rows out of the way. This is a side effect of
    // selection, so it emits no Parent collapsed event; deselecting leaves the
    // expansion state untouched.
    setExpandedIds((current) => {
      if (!current.has(parent.id)) return current;
      const next = new Set(current);
      next.delete(parent.id);
      return next;
    });
    trackEvent('Parent selected', { value: parent.name, children_count: visibleChildCount });
  }

  function parentCheckboxState(parent) {
    return parentStateById.get(parent.id) ?? 'unchecked';
  }

  function isChildLocked(childId) {
    return lockedChildIds.has(childId);
  }

  function toggleExpanded(parent) {
    const expanding = !expandedIds.has(parent.id);

    setExpandedIds((current) => {
      const next = new Set(current);
      if (expanding) next.add(parent.id);
      else next.delete(parent.id);
      return next;
    });
    trackEvent(expanding ? 'Parent expanded' : 'Parent collapsed', { value: parent.name });

    // First expansion pulls the descendants the search didn't match: the
    // response only carries the matching ones, which for a query against the
    // parent's own name is none of them. Fired outside the state updater so a
    // double-invoked updater can't fire the request twice.
    if (expanding && !childFetch[parent.id]) loadChildren(parent, 1);
  }

  function loadChildren(parent, page) {
    if (!childrenPath) return;

    setChildFetch((current) => ({
      ...current,
      [parent.id]: { ...current[parent.id], loading: true, error: false }
    }));

    const request = new AbortableRequest(`${childrenPath}?id=${encodeURIComponent(parent.id)}&page=${page}`, {
      headers: JSON_HEADERS
    });
    childRequestsRef.current.set(parent.id, request);

    request.then((error, response) => {
      // An abort still resolves here, with an error. A new search clears
      // childFetch and aborts whatever was in flight, so without this guard the
      // late rejection writes an error back into the state the new search just
      // reset, and the parent's first expansion then shows a retry row instead
      // of loading. Checking the ref rather than a flag also stops a superseded
      // request from evicting the newer one's entry.
      if (childRequestsRef.current.get(parent.id) !== request) return;
      childRequestsRef.current.delete(parent.id);

      if (error) {
        setChildFetch((current) => ({
          ...current,
          [parent.id]: { ...current[parent.id], loading: false, error: true }
        }));
        return;
      }

      const fetched = response.body?.children ?? [];
      const total = response.body?.total ?? 0;

      // Merge into the results themselves rather than a side store, so the
      // checkbox states, the sole-parent absorption and the aria counts all
      // keep reading one list of children.
      setSearchResults((current) =>
        current.map((result) => {
          if (result.id !== parent.id) return result;

          const existing = result.children ?? [];
          const existingIds = new Set(existing.map((child) => child.id));
          return { ...result, children: [...existing, ...fetched.filter((child) => !existingIds.has(child.id))] };
        })
      );
      setChildFetch((current) => ({
        ...current,
        [parent.id]: {
          loading: false,
          error: false,
          page,
          fetched: (current[parent.id]?.fetched ?? 0) + fetched.length,
          total
        }
      }));
    });
  }

  function removeSelected(item) {
    setSelected((current) => current.filter((entry) => entry.id !== item.id));
    trackEvent('Item deselected', { value: item.name });
  }

  const treeMode = useMemo(() => searchResults.some((item) => Array.isArray(item.children)), [searchResults]);

  // Flatten the result list into the rows that are actually visible (parents
  // plus their expanded children, and the row that pages in the rest).
  // Keyboard nav and rendering both work off this flattened view so a focus
  // index always points at a visible row.
  //
  // Each row carries its own absolute index, so getResultGroups can hand back
  // ordered slices whose rows still render against the index keyboard
  // navigation tracks. Grouping never reorders rows — it only inserts section
  // headers — so index === selectionIndex stays valid across sections.
  const visibleRows = useMemo(() => {
    const rows = [];

    searchResults.forEach((item) => {
      const isParent = hasChildren(item);
      rows.push({ item, isParent, isChild: false, index: rows.length });
      if (!isParent || !expandedIds.has(item.id)) return;

      (item.children ?? []).forEach((child) => {
        rows.push({ item: child, isParent: false, isChild: true, parent: item, index: rows.length });
      });

      // The row that pages in the rest of the subtree, and reports loading or
      // failure while doing so. It is navigable, so it lives in the row list
      // rather than being drawn beside the parent.
      const childState = childFetch[item.id];
      if (childState && (childState.loading || childState.error || childState.fetched < childState.total)) {
        rows.push({ isChildrenStatus: true, parent: item, fetch: childState, index: rows.length });
      }
    });

    return rows;
  }, [searchResults, expandedIds, childFetch]);

  const resultGroups = useMemo(
    () => (getResultGroups ? getResultGroups(visibleRows, responseMeta) : null),
    [getResultGroups, visibleRows, responseMeta]
  );

  function handleKeyDown(event) {
    if (visibleRows.length === 0) return;
    const lastIndex = visibleRows.length - 1;
    const currentRow = selectionIndex === null ? null : visibleRows[selectionIndex];

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectionIndex(selectionIndex === null || selectionIndex === lastIndex ? 0 : selectionIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectionIndex(selectionIndex === null || selectionIndex === 0 ? lastIndex : selectionIndex - 1);
        break;
      case 'ArrowRight':
        if (!currentRow?.isParent) return;
        event.preventDefault();
        if (!expandedIds.has(currentRow.item.id)) {
          toggleExpanded(currentRow.item);
        } else if (visibleRows[selectionIndex + 1]?.parent?.id === currentRow.item.id) {
          // Already expanded — move focus to the first row beneath it (a child,
          // or the row that is still paging them in). An expanded parent whose
          // subtree resolved to nothing has neither, and stepping forward
          // regardless would hand the roving focus to the next root, or push
          // the index past the end of the list.
          setSelectionIndex(selectionIndex + 1);
        }
        break;
      case 'ArrowLeft':
        if (!currentRow) return;
        event.preventDefault();
        if (currentRow.isChild || currentRow.isChildrenStatus) {
          // The paging row sits beneath its parent like a child, so it steps
          // back to it too. Only top-level result rows can be the parent, and
          // the status row carries no `item` at all, so both are filtered out
          // before the id is read.
          const parentIndex = visibleRows.findIndex(
            (row) => !row.isChild && !row.isChildrenStatus && row.item.id === currentRow.parent.id
          );
          if (parentIndex !== -1) setSelectionIndex(parentIndex);
        } else if (currentRow.isParent && expandedIds.has(currentRow.item.id)) {
          toggleExpanded(currentRow.item);
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (currentRow?.isChildrenStatus && !currentRow.fetch.loading) {
          loadChildren(currentRow.parent, (currentRow.fetch.page ?? 0) + 1);
        }
        break;
    }
  }

  // Keep the dropdown mounted across keystrokes whenever there's something to
  // show (current results or a terminal empty/error state). Otherwise each new
  // request flips loading to IN_PROGRESS and the listbox unmounts mid-typing,
  // causing a visible flash.
  const resultsVisible = searchResults.length > 0 || loading === Loading.EMPTY_RESPONSE || loading === Loading.ERROR;
  const isSelected = (id) => selectedIds.has(id);
  const maxReached = maxSelected !== null && selected.length >= maxSelected;

  function renderChildrenStatus(row, index) {
    const { fetch: childState, parent } = row;
    const isFocused = index === selectionIndex;
    // On a failure the state keeps the last page that did land, so the same
    // next page is what both the retry and the normal "show more" ask for.
    const nextPage = (childState.page ?? 0) + 1;

    function label() {
      if (childState.loading) return I18n.t(`${i18nBase}.modal.children_loading`);
      if (childState.error) return I18n.t(`${i18nBase}.modal.children_error`);

      const remaining = childState.total - childState.fetched;
      return I18n.t(`${i18nBase}.modal.children_more`, {
        count: remaining,
        count_formatted: remaining.toLocaleString()
      });
    }

    // The same focusable node across all three states. Rows move focus as the
    // arrow keys walk them, so a loading state with nothing to focus would
    // leave focus on the row above; and swapping the button out for plain text
    // mid-click would drop focus to <body>, where the container's key handler
    // never fires and arrow navigation dies until the user clicks back in.
    return (
      <p
        key={`${parent.id}::status`}
        className="ModalPicker-childrenStatus"
        // A tree may only own treeitem and group nodes, and this row is
        // focusable and arrow-navigable like every other row, so it announces
        // itself as one rather than as an anonymous paragraph the tree
        // navigation skips. aria-live is a global property rather than a role,
        // so the region outlives each state change: a role="status" that only
        // exists while loading is created together with its own text, which is
        // the one case a live region does not announce.
        role={treeMode ? 'treeitem' : undefined}
        aria-level={treeMode ? 2 : undefined}
        aria-live="polite"
      >
        <button
          type="button"
          ref={(node) => {
            if (node && isFocused) node.focus();
          }}
          className="ModalPicker-childrenMore"
          tabIndex={isFocused ? '0' : '-1'}
          aria-disabled={childState.loading || undefined}
          onFocus={() => setSelectionIndex(index)}
          onClick={() => {
            if (!childState.loading) loadChildren(parent, nextPage);
          }}
        >
          {label()}
        </button>
      </p>
    );
  }

  function renderRow(row, index) {
    if (row.isChildrenStatus) return renderChildrenStatus(row, index);

    const lockedByParent = row.isChild && isChildLocked(row.item.id);
    // Unique per visible row: a multi-rooted child appears under each
    // of its parents, so scope the key (and the row's DOM id) by parent.
    const rowKey = row.isChild ? `${row.parent.id}::${row.item.id}` : row.item.id;
    return (
      <ResultRow
        key={rowKey}
        rowId={rowKey}
        item={row.item}
        searchTerm={searchTerm}
        isSelected={isSelected(row.item.id)}
        isFocused={index === selectionIndex}
        disabled={maxReached && !isSelected(row.item.id) && !row.isParent}
        lockedByParent={lockedByParent}
        onToggle={row.isParent ? toggleParentSelected : toggleSelected}
        onFocus={() => setSelectionIndex(index)}
        renderMeta={renderResultMeta}
        renderExtras={renderResultExtras}
        treeMode={treeMode}
        isParent={row.isParent}
        isChild={row.isChild}
        expanded={row.isParent && expandedIds.has(row.item.id)}
        onToggleExpand={toggleExpanded}
        ariaSetSize={row.isChild ? row.parent.children.length : searchResults.length}
        ariaPosInSet={
          row.isChild
            ? row.parent.children.findIndex((c) => c.id === row.item.id) + 1
            : searchResults.findIndex((r) => r.id === row.item.id) + 1
        }
        parentCheckboxState={row.isParent ? parentCheckboxState(row.item) : 'unchecked'}
      />
    );
  }

  return (
    <>
      <h2 className="ModalPicker-header" id={titleId}>
        {I18n.t(`${i18nBase}.modal.title`)}
      </h2>
      <div className="ModalPicker-body">
        <div className="visually-hidden" role="status" aria-live="polite">
          {(loading === Loading.SUCCESS || loading === Loading.EMPTY_RESPONSE) && `${searchResults.length} results`}
        </div>
        <div className="ModalPicker-fieldLabelRow">
          <label htmlFor="ModalPicker-searchInput" className="ModalPicker-fieldLabel">
            {I18n.t(`${i18nBase}.title`)}
          </label>
          <HelpLink
            href={I18n.t('Explorer.AdvancedSearch.help_uri')}
            tip={I18n.t(`${i18nBase}.help`)}
            analyticsFieldName={analyticsDialogueName}
          />
        </div>
        {helperText.length > 0 && (
          <div className="ModalPicker-helperText">
            {helperText.map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        )}
        <div className="ModalPicker-container">
          <div className="ModalPicker-loadingIcon">
            <LoadingIcon loading={loading === Loading.IN_PROGRESS} />
          </div>
          <input
            ref={searchInputRef}
            id="ModalPicker-searchInput"
            role="combobox"
            type="text"
            autoComplete="off"
            aria-autocomplete="list"
            aria-controls={RESULTS_ID}
            aria-expanded={resultsVisible}
            className="ModalPicker-searchInput"
            placeholder={I18n.t(`${i18nBase}.modal.placeholder`)}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onFocus={() => setSelectionIndex(null)}
            onKeyDown={handleKeyDown}
          />
          {resultsVisible && (
            <div className="ModalPicker-resultsContainer">
              <div
                id={RESULTS_ID}
                role={treeMode ? 'tree' : 'listbox'}
                aria-multiselectable="true"
                tabIndex={-1}
                className="ModalPicker-results"
                onKeyDown={handleKeyDown}
              >
                {loading === Loading.EMPTY_RESPONSE && (
                  <p className="ModalPicker-empty-state">
                    {renderNoResults
                      ? renderNoResults(searchTerm)
                      : I18n.t(`${i18nBase}.modal.no_results`, { term: searchTerm })}
                  </p>
                )}
                {loading === Loading.ERROR && (
                  <p className="ModalPicker-error-state" role="alert">
                    {I18n.t(`${i18nBase}.modal.error`)}
                  </p>
                )}
                {loading === Loading.SUCCESS &&
                  (resultGroups
                    ? resultGroups.map((group) => (
                        <div key={group.key} role="group" aria-label={group.label} className="ModalPicker-section">
                          <p className="ModalPicker-sectionLabel">{group.label}</p>
                          {group.rows.map((row) => renderRow(row, row.index))}
                        </div>
                      ))
                    : visibleRows.map((row) => renderRow(row, row.index)))}
              </div>
            </div>
          )}
        </div>
        {maxReached && <p className="ModalPicker-maxReached">{I18n.t(`${i18nBase}.modal.max_reached`)}</p>}
        {renderSelectedList ? (
          renderSelectedList({
            selected,
            onRemove: removeSelected,
            fallbackFocusRef: searchInputRef
          })
        ) : (
          <SelectedList
            selected={selected}
            onRemove={removeSelected}
            fallbackFocusRef={searchInputRef}
            i18nBase={i18nBase}
            renderItemExtras={renderSelectedItemExtras}
          />
        )}
      </div>
      <footer className="ModalPicker-footer">
        <Button variant="danger" onClick={onCancel}>
          {I18n.t(`${i18nBase}.modal.cancel`)}
        </Button>
        <Button variant="confirm" disabled={selected.length === 0} onClick={() => onSave(selected)}>
          {I18n.t(`${i18nBase}.modal.save`)}
        </Button>
      </footer>
    </>
  );
};

Editor.propTypes = {
  initiallySelected: PropTypes.array,
  initialSearch: PropTypes.string,
  titleId: PropTypes.string,
  analyticsDialogueName: PropTypes.string,
  i18nBase: PropTypes.string.isRequired,
  autocompletePath: PropTypes.string.isRequired,
  childrenPath: PropTypes.string,
  trackEvent: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  maxSelected: PropTypes.number,
  helperText: PropTypes.arrayOf(PropTypes.node),
  renderResultMeta: PropTypes.func,
  renderResultExtras: PropTypes.func,
  renderNoResults: PropTypes.func,
  renderSelectedItemExtras: PropTypes.func,
  renderSelectedList: PropTypes.func,
  parseResponse: PropTypes.func,
  getResultGroups: PropTypes.func
};

export default Editor;
